/**
 * 直线元素组件（SVG 根节点，与 rect/triangle/vector 等形状元素一致）
 */
import type { LineElement as LineElementType } from '@/components/canvas/document/publicTypes';
import type { ElementComponentProps } from '@/components/canvas/elements/types';
import { appendBlurFilter, composeContainerTransform } from '../../group/containerTransform';
import { blurRadius, firstStroke } from '@/components/canvas/elements/model';
import { screenPxToWorld } from '@/components/canvas/selection/selectionChrome';

/** 屏幕上至少这么宽才好点选（px） */
const LINE_HIT_STROKE_SCREEN_PX = 12;

export function LineElement({
  element,
  zoom = 1,
  dragOffset = { dx: 0, dy: 0 },
  onSelect,
  onMouseDown,
}: ElementComponentProps<LineElementType>) {
  const { dx, dy } = dragOffset;
  const line = element;
  const stroke = firstStroke(line.appearance);
  const strokeWidth = stroke?.width ?? 2;
  const strokeColor = stroke?.color ?? '#000';
  const start = line.data.start ?? { x: 0, y: 0 };
  const end = line.data.end ?? { x: element.size.width, y: 0 };
  const endX = end.x - start.x;
  const endY = end.y - start.y;
  const ox = Math.min(0, endX);
  const oy = Math.min(0, endY);
  const width = Math.max(1, Math.abs(endX));
  const height = Math.max(1, Math.abs(endY));
  const hitStrokeWidth = Math.max(strokeWidth, screenPxToWorld(LINE_HIT_STROKE_SCREEN_PX, zoom));

  const svgStyle: React.CSSProperties = {
    position: 'absolute',
    left: element.layout.x + start.x + ox + dx,
    top: element.layout.y + start.y + oy + dy,
    overflow: 'visible',
    opacity: line.appearance.opacity,
    transform: composeContainerTransform(line),
    transformOrigin: `${-ox}px ${-oy}px`,
    filter: appendBlurFilter(undefined, blurRadius(line)),
    cursor: 'inherit',
    pointerEvents: 'auto',
  };

  return (
    <svg
      width={width}
      height={height}
      style={svgStyle}
      data-element-hit
      data-element-id={element.id}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(e);
      }}
      onMouseDown={(e) => onMouseDown?.(e, element)}
    >
      <line
        x1={-ox}
        y1={-oy}
        x2={endX - ox}
        y2={endY - oy}
        stroke="transparent"
        strokeWidth={hitStrokeWidth}
        style={{ pointerEvents: 'stroke' }}
      />
      <line
        x1={-ox}
        y1={-oy}
        x2={endX - ox}
        y2={endY - oy}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeDasharray={
          stroke?.style === 'dashed' ? '8,4' : stroke?.style === 'dotted' ? '2,2' : undefined
        }
        style={{ pointerEvents: 'none' }}
      />
    </svg>
  );
}
